// src/components/profile/UserInfo.jsx
import React from 'react';
import { motion } from 'framer-motion';

const UserInfo = ({ 
  userData, 
  isOwnProfile, 
  editSections, 
  formData, 
  handleFormChange, 
  progress, 
  currentLevelXP,
  nextLevelXP,
  BADGE_ICONS
}) => {
  return (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8">
      <div>
        {editSections.about ? (
          <div className="mb-4">
            <input
              type="text"
              value={formData.about.name}
              onChange={(e) => handleFormChange('about', 'name', e.target.value)}
              className="bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 text-2xl font-bold w-full focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your Name"
            />
            <input
              type="text"
              value={formData.about.title}
              onChange={(e) => handleFormChange('about', 'title', e.target.value)}
              className="bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-2 mt-2 text-gray-400 w-full focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your Title (e.g., Certified Graphologist)"
            />
            <div className="flex items-center mt-2">
              <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <input
                type="text"
                value={formData.about.location}
                onChange={(e) => handleFormChange('about', 'location', e.target.value)}
                className="bg-dark-gray/50 border border-gray-700 rounded-md px-4 py-1 text-sm text-gray-400 w-full focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Your Location"
              />
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold flex items-center">
              {userData.name || 'New User'}
              {userData.isVerified && (
                <span className="ml-2 text-blue-500" title="Verified Professional">
                  {BADGE_ICONS.verified}
                </span>
              )}
            </h1>
            <p className="text-gray-400">{userData.title || 'Add your professional title'}</p>
            <p className="text-gray-400 mt-1 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {userData.location || 'Add your location'}
            </p>
          </>
        )}
      </div>
      
      {/* Stats Cards */}
      <div className="mt-6 md:mt-0 flex flex-wrap gap-4">
        {/* Streak Card */}
        <motion.div 
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300, damping: 10 }}
          className="bg-gradient-to-br from-orange-500/20 to-amber-700/20 backdrop-blur-sm border border-orange-500/20 rounded-lg p-3 flex items-center shadow-lg"
        >
          <div className="bg-orange-500/20 rounded-full p-2 mr-3">
            <svg className="w-5 h-5 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2c2 2 4 5 4 8a4 4 0 11-8 0c0-3 2-6 4-8z" />
            </svg>
          </div>
          <div>
            <div className="font-bold text-lg">{userData.streak} Days</div>
            <p className="text-xs text-gray-300">Current Streak</p>
          </div>
        </motion.div>
        
        {/* Level Card */}
        <motion.div 
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300, damping: 10 }}
          className="bg-gradient-to-br from-blue-500/20 to-indigo-700/20 backdrop-blur-sm border border-blue-500/20 rounded-lg p-3 shadow-lg"
        >
          <div className="flex items-center mb-1">
            <div className="bg-blue-500/20 rounded-full p-2 mr-3">
              <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <div className="font-bold text-lg">Level {userData.level}</div>
              <p className="text-xs text-gray-300">{userData.totalXp} Total XP</p>
            </div>
          </div>
          <div className="w-full bg-gray-700/50 rounded-full h-2 mt-1">
            <motion.div 
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
              style={{ width: `${progress}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{currentLevelXP} XP</span>
            <span>{nextLevelXP} XP</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default UserInfo;
